import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Popup } from '@/components/common/Popup/Popup';
import { TextInput } from '@/components/common/TextInput/TextInput';
import {
  Button,
  EButtonHierarchy,
  EButtonSize,
  EButtonStyle,
} from '@/components/common/Button/Button';
import {
  ForgotPasswordSuccessView,
  ForgotPasswordView,
} from '@/pages/Blocked/PasswordProtectionUnlock/components';
import {
  UNLOCK_VARIANT,
  UNLOCK_VIEW,
  usePasswordUnlock,
} from '@/hooks/ui/usePasswordUnlock.hook';
import { selectIsPasswordProtectionLocked } from '@/store/selectors/password-protection.selector';
import { INPUT_TYPE } from '@/constants/ui/input';
import { ReactComponent as ArrowIcon } from '@/assets/icons/actions/password-arrow.svg';
import { ReactComponent as ArrowFilledIcon } from '@/assets/icons/actions/password-arrow-filled.svg';
import styles from './PasswordProtectionLockPopup.module.scss';

const LOCK_POPUP_Z_INDEX = 99999;
const FORGOT_POPUP_Z_INDEX = 100000;

export const PasswordProtectionLockPopup: React.FC = () => {
  const { t } = useTranslation();
  const isLocked = useSelector(selectIsPasswordProtectionLocked);
  const {
    inputError,
    currentView,
    email,
    title,
    subtitle,
    errorText,
    inputValue,
    placeholder,
    onInputValueChange,
    onUnlockClick,
    onForgotPasswordClick,
    onForgotPasswordApprove,
    onForgotPasswordCancel,
  } = usePasswordUnlock({ variant: UNLOCK_VARIANT.DASHBOARD });

  if (!isLocked) return null;

  return (
    <>
      <Popup
        hideCloseButton={true}
        disableBackdropClose={true}
        zIndex={LOCK_POPUP_Z_INDEX}
        popupClassName={styles.card}
        backdropClassName={styles.blurredBackdrop}
        onClose={onForgotPasswordCancel}
      >
        <div className={styles.content}>
          <h2 className={styles.title}>{t(title)}</h2>
          <p className={styles.subtitle}>{t(subtitle)}</p>
          <TextInput
            type={INPUT_TYPE.PASSWORD}
            value={inputValue}
            placeholder={placeholder ? t(placeholder) : ''}
            error={inputError ? t(errorText) : undefined}
            focusOnMount={true}
            onChange={onInputValueChange}
            onEnter={onUnlockClick}
            trailingAction={
              <Button
                size={EButtonSize.xs}
                hierarchy={EButtonHierarchy.neutral}
                buttonStyle={EButtonStyle.ghost}
                iconLead={inputValue ? ArrowFilledIcon : ArrowIcon}
                ariaLabel={t('blockPage.unlock.unlock')}
                onClick={onUnlockClick}
              />
            }
          />
          <Button
            className={styles.forgotButton}
            size={EButtonSize.sm}
            hierarchy={EButtonHierarchy.neutral}
            buttonStyle={EButtonStyle.ghost}
            text="passwordProtectionLock.forgotPassword"
            onClick={onForgotPasswordClick}
          />
        </div>
      </Popup>
      {currentView !== UNLOCK_VIEW.UNLOCK && (
        <Popup
          hideCloseButton={true}
          disableBackdropClose={true}
          zIndex={FORGOT_POPUP_Z_INDEX}
          onClose={onForgotPasswordCancel}
        >
          {({ close }) =>
            currentView === UNLOCK_VIEW.FORGOT_PASSWORD ? (
              <ForgotPasswordView
                onApprove={onForgotPasswordApprove}
                onCancel={close}
              />
            ) : (
              <ForgotPasswordSuccessView email={email} onClose={close} />
            )
          }
        </Popup>
      )}
    </>
  );
};
